import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { groupPermissions } from "@shared/permissions";
import { useMemo } from "react";

type Props = {
    /** permission id -> label, as reported by the backend */
    registered: Record<string, string>;
    value: string[];
    onChange: (permissions: string[]) => void;
    disabled?: boolean;
};

export default function PermissionPicker({ registered, value, onChange, disabled }: Props) {
    const categories = useMemo(() => groupPermissions(registered), [registered]);
    const hasAll = value.includes('all_permissions');

    const toggle = (id: string, checked: boolean) => {
        //"all_permissions" makes every other checkbox meaningless, so picking it
        //clears the rest instead of leaving a contradictory list behind
        if (id === 'all_permissions') {
            onChange(checked ? ['all_permissions'] : []);
            return;
        }
        const next = new Set(value);
        checked ? next.add(id) : next.delete(id);
        next.delete('all_permissions');
        onChange([...next]);
    };

    return (
        <div className="grid gap-4 sm:grid-cols-2">
            {categories.map((category) => (
                <div key={category.label} className="plate p-3 space-y-2">
                    <h4 className="text-label uppercase tracking-[0.1em] text-faint">
                        {category.label}
                    </h4>
                    <div className="space-y-1.5">
                        {category.permissions.map((perm) => {
                            //already covered by all_permissions - show it ticked but locked
                            const covered = hasAll && perm.id !== 'all_permissions';
                            return (
                                <label
                                    key={perm.id}
                                    className={cn(
                                        "flex items-start gap-2 text-sm leading-tight select-none",
                                        disabled || covered ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                                    )}
                                >
                                    <Checkbox
                                        tone={perm.dangerous ? 'danger' : 'accent'}
                                        checked={covered || value.includes(perm.id)}
                                        disabled={disabled || covered}
                                        onCheckedChange={(checked) => toggle(perm.id, checked === true)}
                                    />
                                    <span className={cn("pt-px", perm.dangerous && "text-danger-soft")}>
                                        {perm.label}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
